/**
 * MobileMessageThread — Full-screen mobile message thread overlay.
 * Renders inside MobileShell when navigating to /messages/:id.
 * Handles keyboard resize, auto-scroll, and real-time messages via socket.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Send } from 'lucide-react';
import { getConversation, getMessages, sendMessage } from '../../lib/api-client';
import { useAuth } from '../../context/AuthContext';
import { useSocket } from '../../context/SocketContext';
import resolveAvatar from '../../lib/resolveAvatar';

interface MobileMessageThreadProps {
  conversationId: string;
}

interface ChatMessage {
  id: string;
  text: string;
  fromUserId: number;
  isOwn: boolean;
  at: string;
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const diffH = (now.getTime() - d.getTime()) / 3600000;

  const timeStr = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  if (diffH < 24) return timeStr;
  if (diffH < 168) return `${d.toLocaleDateString([], { weekday: 'short' })} ${timeStr}`;
  return `${d.toLocaleDateString([], { month: 'short', day: 'numeric' })} ${timeStr}`;
}

export function MobileMessageThread({ conversationId }: MobileMessageThreadProps) {
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [otherUser, setOtherUser] = useState<{ displayName: string; avatar?: string }>({ displayName: 'Chat' });
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const { socket } = useSocket();

  // Join conversation room for real-time updates
  useEffect(() => {
    if (!socket || !conversationId) return;
    socket.emit('join', `conversation:${conversationId}`);
    return () => {
      socket.emit('leave', `conversation:${conversationId}`);
    };
  }, [socket, conversationId]);

  // Handle visual viewport resize (keyboard)
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;

    const handleResize = () => {
      const viewportH = vv.height;
      const windowH = window.innerHeight;
      setKeyboardHeight(Math.max(0, windowH - viewportH));
    };

    vv.addEventListener('resize', handleResize);
    return () => vv.removeEventListener('resize', handleResize);
  }, []);

  // Load conversation + messages
  useEffect(() => {
    if (!authUser || !conversationId) return;

    (async () => {
      try {
        const [conv, msgs] = await Promise.all([
          getConversation(conversationId),
          getMessages(conversationId),
        ]);

        // Find the other participant
        // API may return flat { id, username, ... } or nested { userId, user: { ... } }
        const participants = conv.participants as any[] | undefined;
        const other = participants?.find(
          (p) => (p.userId ?? p.id)?.toString() !== authUser.id?.toString()
        );
        if (other) {
          const u = other.user || other;
          setOtherUser({
            displayName: u.profileName || u.displayName || u.name || u.username || 'Chat',
            avatar: resolveAvatar(u.profilePicture || u.image) || undefined,
          });
        }

        const chatMsgs: ChatMessage[] = msgs.map((m: any) => ({
          id: m.id,
          text: m.text,
          fromUserId: m.fromUserId,
          isOwn: m.fromUserId?.toString() === authUser.id?.toString(),
          at: m.at || m.createdAt,
        }));

        setMessages(chatMsgs);
      } catch {
        setError('Failed to load conversation');
      } finally {
        setLoading(false);
      }
    })();
  }, [authUser, conversationId]);

  // Listen for new messages via socket
  useEffect(() => {
    if (!socket) return;

    const handleNewMessage = (msg: any) => {
      if (msg.conversationId === conversationId) {
        setMessages(prev => [...prev, {
          id: msg.id,
          text: msg.text,
          fromUserId: msg.fromUserId,
          isOwn: msg.fromUserId?.toString() === authUser?.id?.toString(),
          at: msg.at || new Date().toISOString(),
        }]);
      }
    };

    socket.on('message:new', handleNewMessage);
    return () => { socket.off('message:new', handleNewMessage); };
  }, [socket, conversationId, authUser]);

  // Auto-scroll on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = useCallback(async () => {
    if (!newMessage.trim() || sending || !authUser) return;
    setSending(true);
    try {
      await sendMessage({ conversationId, text: newMessage.trim() });
      setNewMessage('');
      inputRef.current?.focus();
    } catch { /* silent */ }
    finally { setSending(false); }
  }, [newMessage, sending, authUser, conversationId]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }, [handleSend]);

  if (loading) {
    return (
      <div
        className="fixed inset-0 z-[60] flex items-center justify-center"
        style={{ background: 'var(--app-bg)' }}
      >
        <div
          className="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin"
          style={{ borderColor: 'var(--color-accent)', borderTopColor: 'transparent' }}
        />
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="fixed inset-0 z-[60] flex flex-col items-center justify-center gap-4"
        style={{ background: 'var(--app-bg)' }}
      >
        <span className="text-sm" style={{ color: 'var(--text-color)', opacity: 0.6 }}>{error}</span>
        <button
          onClick={() => navigate(-1)}
          className="px-5 py-2.5 rounded-xl text-sm font-medium"
          style={{ backgroundColor: 'var(--color-accent)', color: '#fff' }}
        >
          Go Back
        </button>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-[60] flex flex-col"
      style={{ background: 'var(--app-bg)' }}
    >
      {/* Header */}
      <div
        className="flex-shrink-0 flex items-center gap-3 px-4"
        style={{
          backgroundColor: 'var(--navbar-bg, rgba(0,0,0,0.9))',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          minHeight: 56,
          paddingTop: 'max(env(safe-area-inset-top, 0px), 8px)',
          paddingBottom: 8,
        }}
      >
        <button
          onClick={() => navigate(-1)}
          className="w-10 h-10 rounded-full flex items-center justify-center"
          style={{ backgroundColor: 'rgba(255,255,255,0.06)' }}
        >
          <ArrowLeft size={20} style={{ color: 'var(--text-color, #fff)' }} />
        </button>
        {otherUser.avatar ? (
          <img src={otherUser.avatar} alt="" className="w-9 h-9 rounded-full object-cover" />
        ) : (
          <div
            className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold"
            style={{ backgroundColor: 'var(--color-accent)', color: 'var(--app-bg)' }}
          >
            {otherUser.displayName.charAt(0).toUpperCase()}
          </div>
        )}
        <h2 className="text-base font-semibold truncate" style={{ color: 'var(--text-color, #fff)' }}>
          {otherUser.displayName}
        </h2>
      </div>

      {/* Messages area */}
      <div
        className="flex-1 overflow-y-auto px-4 py-3 space-y-2"
        style={{ WebkitOverflowScrolling: 'touch' }}
      >
        {messages.length === 0 && (
          <div className="flex items-center justify-center h-full">
            <span className="text-sm" style={{ color: 'var(--text-color)', opacity: 0.4 }}>
              No messages yet. Say hello!
            </span>
          </div>
        )}

        {messages.map(msg => (
          <div
            key={msg.id}
            className={`flex ${msg.isOwn ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className="max-w-[75%] px-4 py-2.5 rounded-2xl"
              style={{
                backgroundColor: msg.isOwn
                  ? 'var(--color-accent, #14b8a6)'
                  : 'var(--card-bg, rgba(255,255,255,0.08))',
                color: msg.isOwn ? '#fff' : 'var(--text-color, #fff)',
              }}
            >
              <p className="text-sm whitespace-pre-wrap break-words">{msg.text}</p>
              <span
                className="text-[10px] block mt-1"
                style={{ opacity: 0.6 }}
              >
                {msg.at ? formatTime(msg.at) : ''}
              </span>
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Input area — adapts to keyboard */}
      <div
        className="flex-shrink-0 flex items-center gap-2 px-4 py-3"
        style={{
          backgroundColor: 'var(--navbar-bg, rgba(0,0,0,0.9))',
          borderTop: '1px solid rgba(255,255,255,0.06)',
          paddingBottom: `max(env(safe-area-inset-bottom, 8px), 8px)`,
          marginBottom: keyboardHeight > 0 ? keyboardHeight : 0,
          transition: 'margin-bottom 0.1s ease-out',
        }}
      >
        <input
          ref={inputRef}
          type="text"
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Type a message..."
          className="flex-1 px-4 py-3 rounded-full text-sm outline-none"
          style={{
            backgroundColor: 'rgba(255,255,255,0.06)',
            color: 'var(--text-color, #fff)',
            border: '1px solid rgba(255,255,255,0.08)',
          }}
        />
        <button
          onClick={handleSend}
          disabled={!newMessage.trim() || sending}
          className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0"
          style={{
            backgroundColor: newMessage.trim() ? 'var(--color-accent, #14b8a6)' : 'rgba(255,255,255,0.06)',
            opacity: newMessage.trim() ? 1 : 0.4,
            transition: 'all 0.15s',
          }}
        >
          <Send size={18} style={{ color: '#fff' }} />
        </button>
      </div>
    </div>
  );
}
